import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";

const Frame = ({ title, desc, children }) => (
  <div className="min-h-screen grid place-items-center px-6 py-12">
    <div className="w-full max-w-md surface p-8 md:p-10 fade-up">
      <Logo className="mb-8" />
      <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
      {desc && <p className="mt-2 text-sm text-muted-foreground">{desc}</p>}
      <div className="mt-6">{children}</div>
      <Link to="/entrar" className="mt-8 inline-block text-sm text-primary hover:underline" data-testid="back-to-login-link">Voltar para o login</Link>
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/forgot-password", { email }); setMsg(data.message); }
    catch (err) { setMsg(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Frame title="Recuperar senha" desc="Informe o e-mail da sua conta. Enviaremos um link seguro, válido por 1 hora.">
      {msg ? (
        <div className="space-y-3 text-sm" data-testid="forgot-password-success">
          <p>{msg}</p>
          <p className="rounded-lg border border-violet-400/30 bg-violet-400/10 p-3 text-xs text-muted-foreground">
            Envio de e-mail SIMULADO nesta versão: o link é registrado no log do servidor e na caixa de saída do painel administrativo. Solicite-o à equipe de suporte.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>E-mail</Label>
            <Input type="email" required className="h-12" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          <Button className="h-12 w-full" disabled={busy} data-testid="forgot-submit-button">Enviar link</Button>
        </form>
      )}
    </Frame>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [state, setState] = useState({ done: false, error: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return setState({ done: false, error: "As senhas não coincidem" });
    try { await api.post("/auth/reset-password", { token: params.get("token"), password: pw.a }); setState({ done: true, error: "" }); }
    catch (err) { setState({ done: false, error: errMsg(err) }); }
  };
  return (
    <Frame title="Definir nova senha" desc="Use ao menos 8 caracteres, com letras e números. Todas as sessões ativas serão encerradas.">
      {state.done ? <p className="text-sm up" data-testid="reset-success">Senha redefinida com sucesso. Faça login novamente.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>Nova senha</Label><Input type="password" required className="h-12" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} data-testid="reset-password-input" /></div>
          <div className="space-y-2"><Label>Confirmar senha</Label><Input type="password" required className="h-12" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} data-testid="reset-password-confirm-input" /></div>
          {state.error && <p className="text-sm down" data-testid="reset-error">{state.error}</p>}
          <Button className="h-12 w-full" data-testid="reset-submit-button">Redefinir senha</Button>
        </form>
      )}
    </Frame>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  useEffect(() => {
    api.post("/auth/verify-email", { token: params.get("token") }).then(() => setState("ok")).catch(() => setState("error"));
  }, [params]);
  return (
    <Frame title="Verificação de e-mail">
      {state === "loading" && <p className="text-sm text-muted-foreground">Verificando…</p>}
      {state === "ok" && <p className="flex items-center gap-2 text-sm up" data-testid="verify-success"><CheckCircle2 className="h-5 w-5" />E-mail confirmado com sucesso.</p>}
      {state === "error" && <p className="flex items-center gap-2 text-sm down" data-testid="verify-error"><XCircle className="h-5 w-5" />Link inválido ou expirado.</p>}
    </Frame>
  );
}
